import { useCallback } from 'react';

import { getPresignedFileUploadData } from '@editor2/services/apiService';

/**
 * Primitive upload hook: fetches a presigned POST policy from the server,
 * uploads the file directly to S3 via XHR (for upload-progress events),
 * and returns the resulting S3 key.
 *
 * The caller is responsible for confirming the upload with the server
 * (confirm_upload or confirm_replace) and updating application state.
 */
export const useS3Upload = () => {
	const upload = useCallback(
		async (file: File, onProgress?: (pct: number) => void): Promise<string> => {
			// Step 1: Get presigned POST policy from our server.
			const { data: presigned } = await getPresignedFileUploadData(file);

			// Step 2: Upload directly to S3 via XHR.
			// XHR is used instead of fetch because it exposes upload progress events.
			// No Authorization header is sent — the presigned POST URL carries S3's own auth.
			const formData = new FormData();
			for (const [field, value] of Object.entries(presigned.fields)) {
				formData.append(field, value);
			}
			formData.append('file', file);

			await new Promise<void>((resolve, reject) => {
				const xhr = new XMLHttpRequest();
				xhr.open('POST', presigned.url);
				xhr.upload.addEventListener('progress', (event) => {
					if (onProgress && event.lengthComputable) {
						onProgress(Math.round((event.loaded * 100) / event.total));
					}
				});
				xhr.addEventListener('load', () => {
					// S3 presigned POST returns 201 on success.
					xhr.status === 201
						? resolve()
						: reject(new Error(`S3 upload failed (HTTP ${xhr.status})`));
				});
				xhr.addEventListener('error', () => reject(new Error('S3 upload network error')));
				xhr.send(formData);
			});

			return presigned.key;
		},
		[]
	);

	return { upload };
};
